import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useUILanguage } from 'hooks/useUILanguage';
import { getCanonicalBaseURL } from 'utils/url';

export function useCurrentUrl(): string {
  const language = useUILanguage();
  const { pathname } = useRouter();
  return `${getCanonicalBaseURL(language)}${pathname}`;
}

export function usePositionAdjustment({
  dialogRef,
  wrapperRef,
  isDialogVisible,
}: {
  dialogRef: React.RefObject<HTMLElement | null>;
  wrapperRef: React.RefObject<HTMLElement | null>;
  isDialogVisible: boolean;
}): void {
  const margin = 10;

  useEffect(() => {
    const dialog = dialogRef.current;
    const wrapper = wrapperRef.current;
    if (!dialog || !wrapper) {
      return;
    }

    const setLeft = (value: number) => {
      if (dialog.style.left !== `${value}px`) {
        dialog.style.left = `${value}px`;
        dialog.style.transformOrigin = `${-value}px 0`;
      }
    };

    const callback = () => {
      const { innerWidth: windowWidth } = window;
      const { offsetLeft: wrapperLeft } = wrapper;
      const { offsetWidth: dialogWidth } = dialog;

      // Render dialog in its natural position when there's space for it
      if (windowWidth - wrapperLeft >= dialogWidth + margin) {
        setLeft(0);
      }
      // Nudge dialog to the left if it would otherwise create overflow
      else if (windowWidth >= dialogWidth + margin * 2) {
        setLeft(windowWidth - wrapperLeft - dialogWidth - margin);
      }
      // Otherwise push it to the left-most position
      else {
        setLeft(-wrapperLeft);
      }
    };

    window.requestAnimationFrame(callback);

    const listener = () => {
      window.requestAnimationFrame(callback);
    };
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [dialogRef, wrapperRef, isDialogVisible]);
}
